import React, { useEffect, useRef, useState } from "react";
import ReactPaginate from "react-paginate";
import Time from "../Times/timesOne";
import Loading from "../common/Loading";
import { getActivities } from "../../services/apiService";

// Service/group filter options (maps to the backend log_name column). The
// delivery console captures order + menu activity under the outlet_* groups;
// auth (login/logout) is shared with the rest of the platform.
const LOG_NAME_OPTIONS = [
  { value: "", label: "All Activities" },
  { value: "auth", label: "Login / Logout" },
  { value: "outlet_order", label: "Orders" },
  { value: "outlet_menu", label: "Menu Management" },
];

// snake_case event -> "Title Case" label
const prettify = (str = "") =>
  str.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// 'App\MenuItem' -> 'Menu Item'
const prettifySubject = (subjectType = "") => {
  const base = subjectType.split("\\").pop() || "";
  return base.replace(/([a-z])([A-Z])/g, "$1 $2");
};

// properties is stored double-wrapped ([ {...} ]) by the logger; normalize it
const normalizeProperties = (properties) => {
  if (!properties) return {};
  if (Array.isArray(properties)) return properties[0] || {};
  return properties;
};

// The affected entity: subject_id is set for most rows; for subjects whose
// foreign key forces subject_id to null, the real id lives in properties.
const resolveEntity = (item) => {
  const props = normalizeProperties(item.properties);
  const id = item.subject_id ?? props.subject_id ?? null;
  const type = item.subject_type ? prettifySubject(item.subject_type) : null;
  if (!type && !id) return null;
  return `${type || "Record"}${id ? ` #${id}` : ""}`;
};

// The acting user: prefer the eager-loaded causer, fall back to the username
// captured in properties (covers rows whose causer relation is missing).
const resolveUser = (item) => {
  const props = normalizeProperties(item.properties);
  const name =
    item.causer?.name ||
    props.causer_name ||
    item.causer?.username ||
    props.causer_username ||
    "—";
  const type = item.causer?.type || null;
  return { name, type };
};

// Cap very long values so a single row can't blow the table width out.
const truncate = (str = "", max = 140) =>
  str.length > max ? str.slice(0, max) + "…" : str;

// Render the field-level diff / created payload for a row
const ActivityDetails = ({ item }) => {
  const props = normalizeProperties(item.properties);
  const changes = props.changes;
  const entity = resolveEntity(item);

  const body = (() => {
    if (changes && Object.keys(changes).length > 0) {
      return (
        <ul className="activity-changes">
          {Object.entries(changes).map(([field, val]) => (
            <li key={field}>
              <b>{prettify(field)}:</b>{" "}
              <span className="old-val">
                {val?.old === null || val?.old === "" ? "—" : String(val?.old)}
              </span>{" "}
              &rarr;{" "}
              <span className="new-val">
                {val?.new === null || val?.new === "" ? "—" : String(val?.new)}
              </span>
            </li>
          ))}
        </ul>
      );
    }

    if (props.attributes && props.attributes.name) {
      return <span>Created &ldquo;{String(props.attributes.name)}&rdquo;</span>;
    }
    if (props.reason) {
      return <span>Reason: {String(props.reason)}</span>;
    }
    if (props.gateway || props.amount) {
      const parts = [];
      if (props.gateway) parts.push(`Gateway: ${String(props.gateway)}`);
      if (props.amount !== undefined && props.amount !== null)
        parts.push(`Amount: ${String(props.amount)}`);
      if (props.refunded_by) parts.push(`By: ${String(props.refunded_by)}`);
      return <span>{parts.join(", ")}</span>;
    }
    if (props.filters && Object.keys(props.filters).length > 0) {
      return (
        <span className="activity-filters">
          {Object.entries(props.filters)
            .map(([k, v]) => {
              const val = typeof v === "object" ? JSON.stringify(v) : String(v);
              return `${prettify(k)}: ${truncate(val)}`;
            })
            .join(", ")}
        </span>
      );
    }
    if (props.device_type) {
      return <span>Device: {String(props.device_type)}</span>;
    }
    return <span>—</span>;
  })();

  return (
    <>
      {entity && <div className="activity-subject">{entity}</div>}
      {body}
    </>
  );
};

const ActivityLog = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [lastPage, setLastPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [logName, setLogName] = useState("");

  const searchRef = useRef();
  const startDateRef = useRef();
  const endDateRef = useRef();

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = {
        page: pageNumber,
        per_page: perPage,
      };
      if (logName) params.log_name = logName;
      if (searchRef.current?.value) params.search = searchRef.current.value;
      if (startDateRef.current?.value)
        params.date_start = startDateRef.current.value;
      if (endDateRef.current?.value) params.date_end = endDateRef.current.value;

      const response = await getActivities(params);
      if (response.status === 200) {
        const paginator = response.data?.data || {};
        setActivities(paginator.data || []);
        setLastPage(paginator.last_page || 0);
        setTotal(paginator.total || 0);
      }
    } catch (error) {
      console.log(error);
      setActivities([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [pageNumber, perPage, logName]);

  const applyFilters = () => {
    if (pageNumber !== 1) {
      setPageNumber(1); // triggers fetch via effect
    } else {
      fetchData();
    }
  };

  const clearFilters = () => {
    if (searchRef.current) searchRef.current.value = "";
    if (startDateRef.current) startDateRef.current.value = "";
    if (endDateRef.current) endDateRef.current.value = "";
    setLogName("");
    setPageNumber(1);
  };

  return (
    <main className="order-history-wrapper revenue_wrapper">
      <div className="aside-left">
        <div className="revenue_title">Activity log filters</div>
        <div className="revenue_main">
          <div className="dates_wrapper">
            <div className="form_group">
              <label htmlFor="log_name">Activity Type:</label>
              <select
                id="log_name"
                name="log_name"
                className="form-control"
                value={logName}
                onChange={(e) => {
                  setLogName(e.target.value);
                  setPageNumber(1);
                }}
              >
                {LOG_NAME_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="form_group">
              <label htmlFor="date_start">Start Date:</label>
              <input
                type="date"
                id="date_start"
                name="date_start"
                ref={startDateRef}
              />
            </div>
            <div className="form_group">
              <label htmlFor="date_end">End Date:</label>
              <input
                type="date"
                id="date_end"
                name="date_end"
                ref={endDateRef}
              />
            </div>
            <div className="form_group">
              <label htmlFor="search">Search description / action</label>
              <input
                type="text"
                id="search"
                name="search"
                ref={searchRef}
                placeholder="Search description / action"
              />
            </div>
            <div className="form_group">
              <div style={{ display: "flex" }}>
                <button
                  className="btn btn-submit"
                  onClick={applyFilters}
                  style={{ flex: "1" }}
                >
                  Apply Filters
                </button>
                <button
                  className="btn btn-submit"
                  onClick={clearFilters}
                  style={{ flex: "1" }}
                >
                  Clear filters
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="aside-right">
        <div className="container-fluid">
          <div
            className="row home-order-screen"
            style={{ position: "relative" }}
          >
            {loading ? (
              <Loading />
            ) : activities.length === 0 ? (
              <div className="col-md-12">
                <div
                  style={{
                    margin: "25vh auto",
                    textAlign: "center",
                    fontSize: "20px",
                    color: "#8e8a8a",
                  }}
                >
                  <p>No Activity Found..</p>
                </div>
              </div>
            ) : (
              <table className="table table-striped table-hover table-history">
                <thead>
                  <tr>
                    <th className="text-center">Date &amp; Time</th>
                    <th className="text-center">User</th>
                    <th className="text-center">Activity</th>
                    <th className="text-center">Description</th>
                    <th className="text-center">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {activities.map((item) => {
                    const user = resolveUser(item);
                    return (
                      <tr key={item.id}>
                        <td className="text-center">
                          <Time
                            newDate={item.created_at}
                            timesString={item.created_at}
                          />
                        </td>
                        <td className="text-center">
                          {user.name}
                          {user.type ? (
                            <div className="activity-user-type">
                              {prettify(user.type)}
                            </div>
                          ) : null}
                        </td>
                        <td className="text-center">{prettify(item.event)}</td>
                        <td>{item.description}</td>
                        <td>
                          <ActivityDetails item={item} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          <div className="pagination-container">
            <div className="totalcount">
              <label>Total Activities: </label>{" "}
              <label>
                <b>{total}</b>
              </label>
            </div>
            <div className="form-group1">
              <label>Row per Page: </label>
              <select
                name="count"
                id="count"
                value={perPage}
                onChange={(e) => {
                  setPerPage(Number(e.target.value));
                  setPageNumber(1);
                }}
              >
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="200">200</option>
              </select>
            </div>
            {lastPage > 1 && (
              <ReactPaginate
                previousLabel={"Previous"}
                nextLabel={"Next"}
                breakLabel={"..."}
                breakClassName={"break-me"}
                pageCount={lastPage}
                forcePage={pageNumber - 1}
                marginPagesDisplayed={2}
                pageRangeDisplayed={2}
                onPageChange={(e) => setPageNumber(e.selected + 1)}
                containerClassName={"pagination"}
                subContainerClassName={"pages pagination"}
                activeClassName={"active"}
              />
            )}
          </div>
        </div>
      </div>
    </main>
  );
};

export default ActivityLog;
